import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckIcon, DownloadIcon, StarIcon, AlertTriangleIcon, RefreshCwIcon, HomeIcon } from 'lucide-react';
import { PageShell } from '../components/layout/PageShell';
import { Button } from '../components/ui/Button';
import { useI18n } from '../lib/i18n';
import { useCart } from '../lib/cart';
import { MACHINE } from '../lib/data';
export function Success() {
  const {
    t,
    lang
  } = useI18n();
  const navigate = useNavigate();
  const {
    lines,
    total,
    clear
  } = useCart();
  const [rating, setRating] = useState(0);
  const [rated, setRated] = useState(false);
  // Fall back to the spec's sample order if the cart was already cleared.
  const displayLines = lines.length > 0 ? lines.map(l => ({
    name: lang === 'en' ? l.drink.name_en : l.drink.name_am,
    volume: l.drink.volume,
    qty: l.qty,
    amount: l.qty * l.drink.price
  })) : [{
    name: lang === 'en' ? 'SinQ' : 'ስንቅ',
    volume: '300ml',
    qty: 3,
    amount: 210
  }];
  const displayTotal = lines.length > 0 ? total : 210;
  const handleReturn = (to: string) => {
    clear();
    navigate(to);
  };
  return <PageShell data-id="element-420" data-mp="13dkt9ne13p82">
      <section className="mx-auto max-w-2xl px-4 py-10 sm:px-6" data-id="element-421" data-mp="13e428elpb39y">
        {/* Header + animated check */}
        <div className="text-center" data-id="element-422" data-mp="20zi9tf8ulkhd">
          <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} transition={{
          type: 'spring',
          stiffness: 200,
          damping: 14
        }} className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-neon neon-glow" data-id="element-423" data-mp="20ygxz8k0ctyl">
            <motion.span initial={{
            scale: 0,
            rotate: -20
          }} animate={{
            scale: 1,
            rotate: 0
          }} transition={{
            delay: 0.25,
            type: 'spring',
            stiffness: 260
          }} data-id="element-424" data-mp="fldtee93h03w">
              <CheckIcon className="h-11 w-11 text-white" strokeWidth={3} data-id="element-425" data-mp="343428ygli7l" />
            </motion.span>
          </motion.div>
          <h1 className="mt-5 text-3xl font-extrabold text-white" data-id="element-426" data-mp="8626ngmdkb3h">
            {t('payment_successful')}
          </h1>
          <p className="mt-2 text-silver/80" data-id="element-427" data-mp="3v8oqs3gv53ww">{t('thank_you')}</p>
        </div>

        {/* Receipt */}
        <div className="glass-card mt-8 p-6" data-id="element-428" data-mp="3gjspcp9bap59">
          <dl className="grid grid-cols-1 gap-y-3 text-sm sm:grid-cols-2" data-id="element-429" data-mp="3gl3o2anwh6pi">
            <div data-id="element-430" data-mp="3li2jcwjavjpa">
              <dt className="text-silver/60" data-id="element-431" data-mp="3ljncqitvowlk">{t('receipt_no')}</dt>
              <dd className="font-semibold text-white" data-id="element-432" data-mp="37ikoq0jbpc80">SATEV-2026-8764</dd>
            </div>
            <div data-id="element-433" data-mp="3bkg243ej3g25">
              <dt className="text-silver/60" data-id="element-434" data-mp="3bkxtalmc3437">{t('transaction_id')}</dt>
              <dd className="font-semibold text-white" data-id="element-435" data-mp="2swdxjtp0d3pw">TXN-78478783</dd>
            </div>
            <div data-id="element-436" data-mp="2sy8hp5sggrsx">
              <dt className="text-silver/60" data-id="element-437" data-mp="2nxkihwu2p4nm">{t('machine')}</dt>
              <dd className="font-semibold text-white" data-id="element-438" data-mp="2nzdpxecifswo">{MACHINE.id}</dd>
            </div>
            <div data-id="element-439" data-mp="31ydekm00w7su">
              <dt className="text-silver/60" data-id="element-440" data-mp="31upjhiexjdc0">{t('date')}</dt>
              <dd className="font-semibold text-white" data-id="element-441" data-mp="2wx7l2lyjggi9">
                Jul 16, 2026 · 11:01 AM
              </dd>
            </div>
          </dl>

          <div className="mt-5 border-t border-[rgba(217,217,217,0.12)] pt-4" data-id="element-442" data-mp="36w6j7alogtr0">
            <p className="mb-2 text-sm font-medium text-silver/70" data-id="element-443" data-mp="36u2i10f5y8uf">
              {t('products')}
            </p>
            <ul className="space-y-1.5 text-sm" data-id="element-444" data-mp="3kpeaf15ujn86">
              {displayLines.map((l, i) => <li key={i} className="flex justify-between text-white" data-id="element-445" data-mp="3goca0h07154m" data-mpi={i}>
                  <span data-id="element-446" data-mp="3um9bnrti81q5">
                    {l.name} {l.volume} × {l.qty}
                  </span>
                  <span data-id="element-447" data-mp="3po7nish81c7t">{l.amount} ETB</span>
                </li>)}
            </ul>
          </div>

          <div className="mt-4 flex items-center justify-between border-t border-[rgba(217,217,217,0.12)] pt-4" data-id="element-448" data-mp="dhahzfhh7065">
            <span className="font-semibold text-white" data-id="element-449" data-mp="him0l11xml4u">{t('total')}</span>
            <span className="text-lg font-bold text-neon" data-id="element-450" data-mp="mcgpciwe9qas">
              {displayTotal} ETB
            </span>
          </div>

          <button type="button" onClick={() => window.print()} className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-neon hover:underline" data-id="element-451" data-mp="3qpwqdhm6fphv">
            <DownloadIcon className="h-4 w-4" data-id="element-452" data-mp="3l76vk4nbr72" />
            {t('download_receipt')}
          </button>
        </div>

        {/* Dispensing status — Completed 100% */}
        <div className="glass-card mt-4 p-5" data-id="element-453" data-mp="8llej12c4z4k">
          <div className="flex items-center justify-between text-sm" data-id="element-454" data-mp="1f0vso865yk6a">
            <span className="font-medium text-white" data-id="element-455" data-mp="1f2qb9ftyq077">
              {t('dispensing_status')}
            </span>
            <span className="font-semibold text-[#4ADE80]" data-id="element-456" data-mp="34hhsaucgliz6">100%</span>
          </div>
          <div className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-[rgba(217,217,217,0.15)]" data-id="element-457" data-mp="34jc4nk9o02rn">
            <motion.div initial={{
            width: 0
          }} animate={{
            width: '100%'
          }} transition={{
            duration: 1,
            ease: 'easeOut'
          }} className="h-full rounded-full bg-[#4ADE80]" data-id="element-458" data-mp="2urokm4ffto07" />
          </div>
          <p className="mt-2 text-sm text-[#4ADE80]" data-id="element-459" data-mp="2gqc4nt0a1meb">
            {t('dispensing_completed')}
          </p>
        </div>

        {/* Rating */}
        <div className="glass-card mt-4 p-5 text-center" data-id="element-460" data-mp="26tr17c3wojcl">
          <p className="text-sm font-medium text-white" data-id="element-461" data-mp="26vagp0hqmqdb">
            {t('rate_experience')}
          </p>
          <div className="mt-3 flex justify-center gap-1" data-id="element-462" data-mp="a0o1zrlaguge">
            {[1, 2, 3, 4, 5].map(n => <button key={n} type="button" onClick={() => {
            setRating(n);
            setRated(true);
          }} aria-label={`${n} stars`} className="p-1" data-id="element-463" data-mp="535um5mx6nai">
                <StarIcon className={`h-8 w-8 transition-colors ${n <= rating ? 'fill-neon text-neon' : 'text-silver/40'}`} data-id="element-464" data-mp="1plzsz76oknmj" />
              </button>)}
          </div>
          {rated && <p className="mt-2 text-sm text-[#4ADE80]" data-id="element-465" data-mp="2i79d1xui59u8">
              {t('thanks_feedback')}
            </p>}
          <a href="https://t.me/Revov_bot" target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-2 text-sm text-silver/70 hover:text-white" data-id="element-466" data-mp="298xgzgav9jue">
            <AlertTriangleIcon className="h-4 w-4 text-neon" data-id="element-467" data-mp="xsfwmekjn7o4" />
            {t('report_problem')}
          </a>
        </div>

        {/* Two buttons */}
        <div className="mt-6 flex flex-col gap-3 sm:flex-row" data-id="element-468" data-mp="ubbg3hunw1kn">
          <Button pulse fullWidth onClick={() => handleReturn('/select/' + MACHINE.id)} data-id="element-469" data-mp="u9grntk6lrbt">
            <RefreshCwIcon className="h-4 w-4" data-id="element-470" data-mp="1497mjwcnm8cf" />
            {t('buy_again')}
          </Button>
          <Button variant="secondary" fullWidth onClick={() => handleReturn('/')} data-id="element-471" data-mp="zaeggozp5jma">
            <HomeIcon className="h-4 w-4" data-id="element-472" data-mp="blvbaiby1myf" />
            {t('return_home')}
          </Button>
        </div>
      </section>
    </PageShell>;
}